import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Award, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PrimaryButton } from "@/components/kit";
import { Field, inputClass } from "@/components/AuthShell";

const CITIES = ["Portland", "Eugene-Springfield", "San Francisco"];
const PLANS = [
  { key: "basic", label: "Basic — $49/mo" },
  { key: "premium", label: "Premium — $149/mo" },
  { key: "undecided", label: "Not sure yet" },
];

export default function FoundingPartner() {
  const [form, setForm] = useState({
    clinic_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: CITIES[0],
    plan_interest: "undecided",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiFetch("/vet-leads", { method: "POST", body: JSON.stringify(form) }, false);
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      <header className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo size={38} to="/" />
        <Link to="/for-vets" className="px-4 py-2 rounded-full font-bold text-[#4A4746] hover:bg-[#F2EFE9]">Why list?</Link>
      </header>

      <section className="max-w-xl mx-auto px-5 sm:px-8 pt-8 pb-16">
        {done ? (
          <div className="text-center py-10" data-testid="founding-success">
            <div className="h-20 w-20 rounded-3xl bg-[#E8F0E5] flex items-center justify-center mx-auto mb-5">
              <PartyPopper size={38} color="#6F8F72" />
            </div>
            <h1 className="font-display text-4xl font-black">Thanks, partner! 🐾</h1>
            <p className="text-[#4A4746] mt-3 text-lg">
              We've received <b>{form.clinic_name}</b>'s details and will reach out about founding-partner perks soon.
            </p>
            <Link to="/" className="inline-block mt-7"><PrimaryButton className="!px-8">Back to home</PrimaryButton></Link>
          </div>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F0E5] text-[#4b6b4e] text-xs font-extrabold uppercase tracking-wide">
              <Award size={13} /> Founding partners
            </span>
            <h1 className="font-display text-5xl font-black leading-[1.05] mt-5">Become a founding vet partner.</h1>
            <p className="text-lg text-[#4A4746] mt-4">
              Get featured placement, analytics, and one-tap calls from local dog parents — with founding-partner pricing locked in.
            </p>

            <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-[#E8E3DA] p-6">
              <Field label="Clinic name">
                <input data-testid="founding-clinic-input" required value={form.clinic_name} onChange={set("clinic_name")} placeholder="Riverside Animal Hospital" className={inputClass} />
              </Field>
              <Field label="Your name">
                <input data-testid="founding-contact-input" required value={form.contact_name} onChange={set("contact_name")} placeholder="Dr. Jane Doe" className={inputClass} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Email">
                  <input data-testid="founding-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="clinic@example.com" className={inputClass} />
                </Field>
                <Field label="Phone">
                  <input data-testid="founding-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 503 555 0100" className={inputClass} />
                </Field>
              </div>
              <Field label="City">
                <select data-testid="founding-city-select" value={form.city} onChange={set("city")} className={inputClass}>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Plan interest">
                <select data-testid="founding-plan-select" value={form.plan_interest} onChange={set("plan_interest")} className={inputClass}>
                  {PLANS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Anything else? (optional)">
                <textarea data-testid="founding-message-input" rows={2} value={form.message} onChange={set("message")} className={inputClass} placeholder="Tell us about your clinic…" />
              </Field>
              <PrimaryButton data-testid="founding-submit-btn" type="submit" loading={loading} className="w-full">
                Apply as founding partner
              </PrimaryButton>
              <p className="flex items-center gap-1.5 mt-4 text-sm text-[#4A4746] justify-center">
                <Check size={15} color="#6F8F72" /> No commitment — we'll share details first
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
